import { LifeBiographyRuntime, type BiographyLOD, type BiographyRecord } from "./life-biography-runtime";
import { LifeCausalityGraph, type LifeCausalitySnapshot, type LifeRelation } from "./life-causality";
import { LivingNexus, type NexusSnapshot } from "./nexus";
import { AkhProtocol, type AkhPolicy, type AkhSnapshot } from "./akh-protocol";
import {
  OmegaSpectrumEngine,
  domainSignalEmitter,
  harmonicProjectionEmitter,
  lifeSpectralEmitter,
  physicalFrequencyProjection,
  type DomainProjectionOptions,
  type DomainSignalFrame,
  type HarmonicProjectionFrame,
  type HarmonicProjectionOptions,
  type OmegaSpectrumSnapshot,
  type PhysicalFrequencyFrame,
  type PhysicalFrequencyProjectionResult,
  type SpectralEmitter,
} from "./omega-spectrum";
import {
  OmegaGenomeRegistry,
  type OmegaAssociation,
  type OmegaEffectChannel,
  type OmegaEffectProposal,
  type OmegaGenomeSnapshot,
  type OmegaOutcomeSample,
} from "./omega-genome";
import {
  QuantumVacuumRuntime,
  type QuantumVacuumCell,
  type QuantumVacuumProposal,
  type QuantumVacuumSnapshot,
  type QuantumVacuumState,
} from "./quantum-vacuum-runtime";
import { RealityBridge } from "./reality-bridge";
import { DimensionBridgeCore, type DimensionBridgeSnapshot } from "./dimensional-bridge";
import { DimensionalEvolutionDirector, type DimensionalEvolutionProposal } from "./dimensional-evolution";
import { SovereignMind } from "./sovereign-mind";
import {
  UniversalLifeRuntime,
  type LifeEntity,
  type LifeObservation,
  type LifeProfile,
  type LifeVec3,
  type UniversalLifeSnapshot,
} from "./universal-life-runtime";
import type { EconomyState, EntityContinuity, HabitatState } from "./continuity";

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const finite = (value: unknown, fallback = 0) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

export interface LivingUniverseSnapshot {
  version: 1;
  gameId: string;
  worldId: string;
  savedAtMs: number;
  runtime: UniversalLifeSnapshot;
  causality: LifeCausalitySnapshot;
  biography: BiographyRecord[];
  continuity: EntityContinuity[];
  habitats: HabitatState[];
  economy: EconomyState;
  nexus: NexusSnapshot;
  spectrum?: OmegaSpectrumSnapshot;
  omegaGenome?: OmegaGenomeSnapshot;
  vacuum?: QuantumVacuumSnapshot;
  akh?: AkhSnapshot;
  dimensions?: DimensionBridgeSnapshot;
  spectrumAccumulator?: number;
  spectrumPrimed?: boolean;
}

export interface LivingUniverseOptions {
  gameId: string;
  worldId?: string;
  tickBudget?: number;
  mindBudget?: number;
  /** Privileged approval and evidence verification must come from the host. */
  akhPolicy?: AkhPolicy;
  spectrumHz?: number;
  maxOfflineSeconds?: number;
  causality?: LifeCausalityGraph;
  biography?: LifeBiographyRuntime;
  runtime?: UniversalLifeRuntime;
  spectrum?: OmegaSpectrumEngine;
  omegaGenome?: OmegaGenomeRegistry;
  vacuum?: QuantumVacuumRuntime;
}

export interface RestoreResult {
  entities: number;
  biographies: number;
  causes: number;
  relationships: number;
  offlineSeconds: number;
}

export class LivingUniverseCore {
  readonly gameId: string;
  readonly worldId: string;
  readonly causality: LifeCausalityGraph;
  readonly biography: LifeBiographyRuntime;
  readonly runtime: UniversalLifeRuntime;
  readonly nexus = new LivingNexus();
  readonly spectrum: OmegaSpectrumEngine;
  readonly omegaGenome: OmegaGenomeRegistry;
  readonly vacuum: QuantumVacuumRuntime;
  readonly reality = new RealityBridge();
  readonly dimensions = new DimensionBridgeCore();
  readonly dimensionEvolution = new DimensionalEvolutionDirector();
  readonly akh: AkhProtocol;
  private akhRestoreTask: Promise<void> = Promise.resolve();
  private akhRestoreError: Error | null = null;

  private readonly mind = new SovereignMind();
  private readonly continuity = new Map<string, EntityContinuity>();
  private readonly habitats = new Map<string, HabitatState>();
  private readonly mindBudget: number;
  private readonly spectrumIntervalSeconds: number;
  private readonly maxOfflineSeconds: number;
  private mindAccumulator = 0;
  private mindCursor = 0;
  private spectrumAccumulator = 0;
  private spectrumPrimed = false;
  private lastVacuumProposalAtMs = -1;
  private economy: EconomyState = {
    currencySupply: 0,
    tradeActivity: 0,
    production: 0,
    scarcity: 0.5,
    lastUpdatedAt: 0,
  };

  constructor(options: LivingUniverseOptions) {
    this.gameId = options.gameId;
    this.worldId = options.worldId ?? options.gameId;
    // Every core observes critical Nexus events; the default policy cannot approve or execute.
    this.akh = new AkhProtocol({ gameId: this.gameId, worldId: this.worldId, nexus: this.nexus, policy: options.akhPolicy });
    // Shared observer: executable simulation returns bounded metadata, never private Nexus payloads.
    this.akh.register("nexus.observe", {
      simulate: item => {
        const eventId = item.causeIds[0] ?? null;
        const event = this.nexus.recent().find(record => record.id === eventId);
        const reported = item.payload as { eventType?: string } | undefined;
        return {
          eventId,
          eventType: event?.type ?? reported?.eventType ?? null,
          observed: Boolean(event),
          atMs: event?.atMs ?? item.atMs,
        };
      },
      // An actual review remains subject to external certification, approval and capability gates.
      execute: item => ({ eventId: item.causeIds[0] ?? null, reviewed: true }),
    });
    this.nexus.subscribe(event => {
      void this.akh.observeNexus(event).catch(() => { /* fail closed without interrupting gameplay */ });
    });
    // Dimensional simulation is part of the same canonical universe authority.
    // Events are projected into Nexus as bounded simulation metadata only; this
    // never grants RealityBridge/device capabilities.
    this.dimensions.onEvent(event => {
      this.nexus.publish({
        atMs: event.atMs,
        gameId: this.gameId,
        worldId: this.worldId,
        type: `dimension.${event.type}`,
        source: "dimension-bridge",
        entityId: "entityId" in event ? event.entityId : undefined,
        payload: { ...event },
      });
      const proposals = this.dimensionEvolution.observe(event, this.dimensions.snapshot(event.atMs));
      for (const proposal of proposals) {
        this.nexus.publish({
          atMs: proposal.atMs,
          gameId: this.gameId,
          worldId: this.worldId,
          type: "dimension.evolution-proposal",
          source: "dimension-evolution-director",
          payload: proposal,
        });
      }
    });
    this.mindBudget = Math.max(1, Math.trunc(options.mindBudget ?? 64));
    const spectrumHz = Math.max(0.5, Math.min(60, finite(options.spectrumHz, 4)));
    this.spectrumIntervalSeconds = 1 / spectrumHz;
    this.maxOfflineSeconds = Math.max(0, options.maxOfflineSeconds ?? 7 * 24 * 60 * 60);
    this.causality = options.causality ?? new LifeCausalityGraph();
    this.biography = options.biography ?? new LifeBiographyRuntime();
    this.runtime = options.runtime ?? new UniversalLifeRuntime({
      tickBudget: options.tickBudget,
      causality: this.causality,
    });
    this.spectrum = options.spectrum ?? new OmegaSpectrumEngine();
    this.omegaGenome = options.omegaGenome ?? new OmegaGenomeRegistry();
    this.vacuum = options.vacuum ?? new QuantumVacuumRuntime();
    this.vacuum.setEventSink(event => {
      const atMs = Math.max(0, Math.round(event.atSeconds * 1000));
      this.nexus.publish({
        atMs,
        gameId: this.gameId,
        worldId: this.worldId,
        type: event.type,
        source: "quantum-vacuum-runtime",
        payload: event.payload,
      });
      if (
        event.type === "vacuum.phase-transition" &&
        atMs !== this.lastVacuumProposalAtMs
      ) {
        this.lastVacuumProposalAtMs = atMs;
        this.publishVacuumEffectProposals(6, atMs);
      }
    });
    this.spectrum.setEventSink(event => {
      const atMs = Math.max(0, Math.round(event.atSeconds * 1000));
      this.nexus.publish({
        atMs,
        gameId: this.gameId,
        worldId: this.worldId,
        type: event.type,
        source: "omega-spectrum",
        payload: event.payload,
      });
      if (event.type === "spectrum.mode.reinforced" && "id" in event.payload) {
        this.publishSpectralEffectProposals(event.payload.id, 0.65, 4, atMs);
      }
    });
  }

  register(input: Partial<LifeEntity> & { id: string; profile: LifeProfile; position: LifeVec3 }, habitatId = "unbound"): LifeEntity {
    const entity = this.runtime.register(input);
    this.biography.register(entity.id, entity.profile.speciesId, this.runtime.nowSeconds(), entity.ageSeconds, {
      lod: this.biographyLOD(entity.tier),
    });
    if (!this.continuity.has(entity.id)) {
      this.continuity.set(entity.id, {
        entityId: entity.id,
        habitatId,
        occupation: "unassigned",
        wealth: 0,
        reputation: 0,
        goals: [],
        lastMindAt: this.runtime.nowSeconds(),
      });
    }
    this.ensureHabitat(habitatId);
    this.recountHabitats();
    this.spectrum.register(lifeSpectralEmitter(entity));
    return entity;
  }

  /** Abort a freshly admitted being before it becomes a committed world birth.
   * Never call this for established living entities: no death or legacy is emitted.
   */
  rollbackUncommittedRegistration(entityId: string): boolean {
    if (!this.runtime.get(entityId)) return false;
    this.runtime.unregister(entityId);
    this.biography.unregister(entityId);
    this.continuity.delete(entityId);
    this.causality.rollbackUncommittedEntity(entityId);
    this.spectrum.unregister("life:" + entityId);
    this.recountHabitats();
    return true;
  }

  observe(observation: LifeObservation): void {
    this.runtime.observe(observation);
    const entity = this.runtime.get(observation.entityId);
    if (!entity) return;
    this.biography.recordEvent(entity.id, {
      at: this.runtime.nowSeconds(),
      kind: observation.kind,
      summary: observation.summary ?? observation.kind,
      salience: clamp01(observation.intensity ?? 0.5),
      actorIds: observation.targetId ? [entity.id, observation.targetId] : [entity.id],
    });
    this.learnSpectrumFromObservation(observation, entity);
  }

  setHabitat(entityId: string, habitatId: string): void {
    const state = this.continuity.get(entityId);
    if (!state) return;
    state.habitatId = habitatId;
    this.ensureHabitat(habitatId);
    this.recountHabitats();
  }

  setOccupation(entityId: string, occupation: string): void {
    const state = this.continuity.get(entityId);
    if (state) state.occupation = occupation;
  }

  setOffscreen(entityId: string): void {
    this.biography.setLOD(entityId, "offscreen", this.runtime.nowSeconds());
    const entity = this.runtime.get(entityId);
    if (entity) entity.tier = "dormant";
  }

  setVisible(entityId: string, lod: "heroic" | "active" | "ambient"): void {
    this.biography.setLOD(entityId, lod, this.runtime.nowSeconds());
    const entity = this.runtime.get(entityId);
    if (entity) entity.tier = lod;
  }

  relate(sourceId: string, targetId: string, relation: LifeRelation, value: number, summary: string = relation): void {
    const cause = this.causality.record({
      at: this.runtime.nowSeconds(),
      kind: "interaction",
      actorId: sourceId,
      targetId,
      summary,
      salience: clamp01(Math.abs(value)),
    });
    this.causality.relate(sourceId, targetId, relation, value, this.runtime.nowSeconds(), cause.id);
  }

  kill(entityId: string, summary = "life ended"): void {
    this.runtime.kill(entityId, summary);
    this.biography.kill(entityId, this.runtime.nowSeconds(), summary);
    this.spectrum.unregister("life:" + entityId);
    this.recountHabitats();
  }

  revive(entityId: string): LifeEntity | undefined {
    const entity = this.runtime.revive(entityId);
    if (!entity) return undefined;
    const record = this.biography.get(entityId);
    if (record?.stage === "dead") {
      record.stage = "adult";
      record.deathAt = undefined;
      record.lastSimulatedAt = this.runtime.nowSeconds();
    }
    this.spectrum.register(lifeSpectralEmitter(entity));
    this.recountHabitats();
    return entity;
  }

  tick(dtSeconds: number, atMs = Date.now()): void {
    if (!(dtSeconds > 0) || !Number.isFinite(dtSeconds)) return;
    this.runtime.tick(dtSeconds);
    this.biography.advanceOffscreen(this.runtime.nowSeconds());
    this.mindAccumulator += dtSeconds;
    if (this.mindAccumulator >= 2) {
      this.mindAccumulator %= 2;
      this.runMindPass();
      this.updateEconomy(2, atMs);
    }
    if (!this.spectrumPrimed) {
      this.runSpectrumPass(dtSeconds, atMs);
      this.spectrumPrimed = true;
      this.spectrumAccumulator = 0;
    } else {
      this.spectrumAccumulator += dtSeconds;
      if (this.spectrumAccumulator >= this.spectrumIntervalSeconds) {
        const spectralDt = this.spectrumAccumulator;
        this.spectrumAccumulator %= this.spectrumIntervalSeconds;
        this.runSpectrumPass(spectralDt, atMs);
      }
    }
  }

  fastForward(elapsedSeconds: number, atMs = Date.now()): number {
    const elapsed = this.runtime.fastForward(elapsedSeconds, this.maxOfflineSeconds);
    if (!elapsed) return 0;
    this.biography.fastForward(elapsed, this.runtime.nowSeconds());
    this.advanceContinuity(elapsed);
    this.updateEconomy(elapsed, atMs);
    this.runMindPass();
    this.runSpectrumPass(Math.max(1 / 60, Math.min(elapsed, 60)), atMs);
    this.spectrumPrimed = true;
    this.spectrumAccumulator = 0;
    this.nexus.publish({
      atMs,
      gameId: this.gameId,
      worldId: this.worldId,
      type: "offline-catchup",
      source: "living-universe",
      payload: { elapsedSeconds: elapsed, population: this.runtime.all().filter(entity => entity.alive).length },
    });
    return elapsed;
  }

  snapshot(savedAtMs = Date.now()): LivingUniverseSnapshot {
    return {
      version: 1,
      gameId: this.gameId,
      worldId: this.worldId,
      savedAtMs,
      runtime: this.runtime.snapshot(),
      causality: this.causality.snapshot(),
      biography: this.biography.snapshot(),
      continuity: [...this.continuity.values()].map(this.cloneContinuity),
      habitats: [...this.habitats.values()].map(habitat => ({ ...habitat })),
      economy: { ...this.economy },
      nexus: this.nexus.snapshot(),
      spectrum: this.spectrum.snapshot(),
      omegaGenome: this.omegaGenome.snapshot(),
      vacuum: this.vacuum.snapshot(),
      akh: this.akh.snapshot(),
      dimensions: this.dimensions.snapshot(savedAtMs),
      spectrumAccumulator: this.spectrumAccumulator,
      spectrumPrimed: this.spectrumPrimed,
    };
  }

  restore(snapshot: unknown, atMs = Date.now()): RestoreResult {
    if (!snapshot || typeof snapshot !== "object") return this.emptyRestore();
    const candidate = snapshot as Partial<LivingUniverseSnapshot>;
    if (candidate.version !== 1 || candidate.gameId !== this.gameId || candidate.worldId !== this.worldId) return this.emptyRestore();

    const causality = this.causality.restore(candidate.causality);
    const entities = this.runtime.restore(candidate.runtime);
    const biographies = this.biography.restore(candidate.biography);

    this.continuity.clear();
    for (const state of Array.isArray(candidate.continuity) ? candidate.continuity : []) {
      if (!state?.entityId) continue;
      this.continuity.set(state.entityId, this.cloneContinuity(state));
    }

    this.habitats.clear();
    for (const habitat of Array.isArray(candidate.habitats) ? candidate.habitats : []) {
      if (habitat?.id) this.habitats.set(habitat.id, { ...habitat });
    }

    if (candidate.economy) this.economy = { ...candidate.economy };
    this.nexus.restore(candidate.nexus);
    this.akhRestoreError = null;
    if (candidate.akh) {
      this.akhRestoreTask = this.akh.restore(candidate.akh).then(() => undefined).catch(error => {
        this.akhRestoreError = error instanceof Error ? error : new Error(String(error));
      });
    }
    if (candidate.spectrum) this.spectrum.restore(candidate.spectrum);
    else this.syncSpectrum();
    if (candidate.omegaGenome) this.omegaGenome.restore(candidate.omegaGenome);
    else this.omegaGenome.clear();
    if (candidate.vacuum) this.vacuum.restore(candidate.vacuum);
    else this.vacuum.clear();
    this.dimensionEvolution.clear();
    if (candidate.dimensions) this.dimensions.restore(candidate.dimensions, atMs);
    this.spectrumAccumulator = Math.max(
      0,
      Math.min(this.spectrumIntervalSeconds, finite(candidate.spectrumAccumulator)),
    );
    this.spectrumPrimed = candidate.spectrumPrimed ?? Boolean(candidate.spectrum);
    this.recountHabitats();

    const savedAtMs = Math.max(0, finite(candidate.savedAtMs, atMs));
    const offlineSeconds = this.fastForward(Math.max(0, (atMs - savedAtMs) / 1000), atMs);
    return { entities, biographies, ...causality, offlineSeconds };
  }

  /** Await signed ledger validation when loading a snapshot; never trust a partial restore. */
  async waitForAkhRestore(): Promise<void> {
    await this.akhRestoreTask;
    if (this.akhRestoreError) throw this.akhRestoreError;
  }

  entityState(entityId: string): EntityContinuity | undefined {
    const state = this.continuity.get(entityId);
    return state ? this.cloneContinuity(state) : undefined;
  }

  habitatState(habitatId: string): HabitatState | undefined {
    const state = this.habitats.get(habitatId);
    return state ? { ...state } : undefined;
  }

  economyState(): EconomyState { return { ...this.economy }; }

  vacuumState(): QuantumVacuumState {
    return this.vacuum.currentState();
  }

  vacuumProposals(limit = 8): QuantumVacuumProposal[] {
    return this.vacuum.proposals(limit);
  }

  dimensionalEvolutionProposals(limit = 16): DimensionalEvolutionProposal[] {
    return this.dimensionEvolution.recent(limit);
  }

  publishVacuumEffectProposals(
    limit = 6,
    atMs = Date.now(),
  ): QuantumVacuumProposal[] {
    const proposals = this.vacuum.proposals(limit);
    for (const proposal of proposals) {
      this.nexus.publish({
        atMs: Math.max(0, Math.round(atMs)),
        gameId: this.gameId,
        worldId: this.worldId,
        type: "vacuum.effect.proposed",
        source: "quantum-vacuum-runtime",
        payload: proposal,
      });
    }
    return proposals;
  }

  vacuumCellAt(position: LifeVec3): QuantumVacuumCell | undefined {
    return this.vacuum.sampleAt(position);
  }

  ingestDomainSignal(
    frame: DomainSignalFrame,
    options: DomainProjectionOptions,
  ): SpectralEmitter {
    const emitter = domainSignalEmitter(frame, options);
    return this.spectrum.register(emitter);
  }

  removeSpectralEmitter(id: string): boolean {
    return this.spectrum.unregister(id);
  }

  ingestHarmonicProjection(
    frame: HarmonicProjectionFrame,
    options: HarmonicProjectionOptions,
  ): SpectralEmitter {
    const emitter = harmonicProjectionEmitter(frame, options);
    return this.spectrum.register(emitter);
  }

  ingestPhysicalFrequencyProjection(
    frame: PhysicalFrequencyFrame,
    options: DomainProjectionOptions,
  ): PhysicalFrequencyProjectionResult {
    const projection = physicalFrequencyProjection(frame, options);
    for (const emitter of projection.emitters) this.spectrum.register(emitter);
    return {
      ...projection,
      emitters: projection.emitters.map(emitter => this.spectrum.emitter(emitter.id) ?? emitter),
    };
  }

  observeSpectralOutcome(sample: OmegaOutcomeSample): OmegaAssociation | undefined {
    const mode = this.spectrum.mode(sample.modeId);
    const association = this.omegaGenome.observe({
      ...sample,
      modeStability: sample.modeStability ?? mode?.stability,
    });
    if (association) {
      const milestone = association.evidence === 1 ||
        (association.evidence & (association.evidence - 1)) === 0;
      if (milestone) {
        const atMs = Math.max(0, Math.round(sample.atSeconds * 1000));
        this.nexus.publish({
          atMs,
          gameId: this.gameId,
          worldId: this.worldId,
          type: "spectrum.association.updated",
          source: "omega-genome",
          payload: association,
        });
        this.publishSpectralEffectProposals(sample.modeId, 0.65, 4, atMs);
      }
    }
    return association;
  }

  spectralEffectProposals(modeId: string, minConfidence = 0.55, limit = 8): OmegaEffectProposal[] {
    const mode = this.spectrum.mode(modeId);
    return mode ? this.omegaGenome.proposalsForMode(mode, minConfidence, limit) : [];
  }

  publishSpectralEffectProposals(
    modeId: string,
    minConfidence = 0.65,
    limit = 4,
    atMs = Date.now(),
  ): OmegaEffectProposal[] {
    const proposals = this.spectralEffectProposals(modeId, minConfidence, limit);
    for (const proposal of proposals) {
      this.nexus.publish({
        atMs: Math.max(0, Math.round(atMs)),
        gameId: this.gameId,
        worldId: this.worldId,
        type: "spectrum.effect.proposed",
        source: "omega-genome",
        payload: proposal,
      });
    }
    return proposals;
  }

  private learnSpectrumFromObservation(observation: LifeObservation, entity: LifeEntity): void {
    const modes = this.spectrum.strongestModes(4)
      .filter(mode => mode.amplitude >= 0.05 && mode.stability >= 0.2)
      .slice(0, 2);
    if (!modes.length) return;

    const intensity = clamp01(observation.intensity ?? 0.5);
    const outcome = this.observationSpectralOutcome(observation.kind, intensity);
    const contextTags = [
      `observation:${observation.kind}`,
      `species:${entity.profile.speciesId}`,
      ...(observation.targetId ? ["targeted"] : []),
    ];

    for (const mode of modes) {
      this.observeSpectralOutcome({
        modeId: mode.id,
        channel: outcome.channel,
        effect: outcome.effect,
        value: outcome.value,
        atSeconds: mode.lastSeenAt,
        modeStability: mode.stability,
        contextTags,
      });
    }
  }

  private observationSpectralOutcome(
    kind: LifeObservation["kind"],
    intensity: number,
  ): { channel: OmegaEffectChannel; effect: string; value: number } {
    switch (kind) {
      case "threat":
        return { channel: "combat", effect: "observation:threat-pressure", value: intensity };
      case "success":
        return { channel: "mind", effect: "observation:success-valence", value: intensity };
      case "failure":
        return { channel: "mind", effect: "observation:failure-valence", value: -intensity };
      case "food":
      case "water":
      case "shelter":
        return { channel: "life", effect: `observation:${kind}-availability`, value: intensity };
      case "individual":
        return { channel: "mind", effect: "observation:social-contact", value: intensity };
      case "place":
        return { channel: "navigation", effect: "observation:place-salience", value: intensity };
      case "event":
      default:
        return { channel: "system", effect: "observation:event-salience", value: intensity };
    }
  }

  private runSpectrumPass(dtSeconds: number, atMs: number): void {
    this.syncSpectrum();
    const atSeconds = atMs / 1000;
    this.spectrum.tick(
      atSeconds,
      Math.max(1 / 1000, Math.min(60, finite(dtSeconds, 1 / 60))),
    );
    const modes = this.spectrum.strongestModes(64);
    this.vacuum.update({
      atSeconds,
      modes,
      emitters: this.spectrum.emittersForModes(modes, 256),
    });
  }

  private syncSpectrum(): void {
    for (const entity of this.runtime.all()) {
      const id = "life:" + entity.id;
      if (entity.alive) this.spectrum.register(lifeSpectralEmitter(entity));
      else this.spectrum.unregister(id);
    }
  }

  private runMindPass(): void {
    const living = this.runtime.all().filter(entity => entity.alive).sort((a, b) => a.id.localeCompare(b.id));
    if (!living.length) return;
    for (let index = 0; index < Math.min(this.mindBudget, living.length); index += 1) {
      const entity = living[this.mindCursor % living.length];
      this.mindCursor = (this.mindCursor + 1) % living.length;
      const state = this.continuity.get(entity.id);
      if (state) this.mind.reconcile(entity, state, this.runtime.nowSeconds());
    }
  }

  private advanceContinuity(elapsedSeconds: number): void {
    const hours = elapsedSeconds / 3600;
    for (const state of this.continuity.values()) {
      const entity = this.runtime.get(state.entityId);
      if (!entity?.alive) continue;
      if (state.occupation !== "unassigned") state.wealth = Math.max(0, state.wealth + hours * 0.08);
      const active = state.goals.find(goal => goal.status === "active");
      if (active) active.progress = clamp01(active.progress + Math.min(0.35, hours * 0.004));
    }
  }

  private updateEconomy(elapsedSeconds: number, atMs: number): void {
    const living = this.runtime.all().filter(entity => entity.alive);
    const workers = living.filter(entity => entity.action === "work").length;
    const traders = living.filter(entity => entity.action === "socialize" || entity.action === "travel").length;
    const productionTarget = living.length ? workers / living.length : 0;
    const tradeTarget = living.length ? traders / living.length : 0;
    const smoothing = clamp01(elapsedSeconds / 300);
    this.economy.production += (productionTarget - this.economy.production) * smoothing;
    this.economy.tradeActivity += (tradeTarget - this.economy.tradeActivity) * smoothing;
    this.economy.currencySupply = Math.max(0, this.economy.currencySupply + workers * elapsedSeconds / 3600 * 0.1);
    this.economy.scarcity = clamp01(0.6 - this.economy.production * 0.35 + (1 - this.economy.tradeActivity) * 0.15);
    this.economy.lastUpdatedAt = atMs;

    for (const habitat of this.habitats.values()) {
      habitat.activity = clamp01((habitat.population / 50) * 0.5 + this.economy.tradeActivity * 0.5);
      habitat.prosperity = clamp01((1 - this.economy.scarcity) * 0.6 + habitat.resources * 0.4);
      habitat.lastUpdatedAt = atMs;
    }
  }

  private ensureHabitat(id: string): HabitatState {
    const current = this.habitats.get(id);
    if (current) return current;
    const created: HabitatState = {
      id,
      population: 0,
      resources: 0.5,
      prosperity: 0.5,
      safety: 0.5,
      activity: 0,
      lastUpdatedAt: 0,
    };
    this.habitats.set(id, created);
    return created;
  }

  private recountHabitats(): void {
    for (const habitat of this.habitats.values()) habitat.population = 0;
    for (const state of this.continuity.values()) {
      if (!this.runtime.get(state.entityId)?.alive) continue;
      this.ensureHabitat(state.habitatId).population += 1;
    }
  }

  private biographyLOD(tier: LifeEntity["tier"]): BiographyLOD {
    return tier === "dormant" ? "offscreen" : tier;
  }

  private cloneContinuity = (state: EntityContinuity): EntityContinuity => ({
    ...state,
    goals: state.goals.map(goal => ({ ...goal })),
  });

  private emptyRestore(): RestoreResult {
    return { entities: 0, biographies: 0, causes: 0, relationships: 0, offlineSeconds: 0 };
  }
}